import api from '../utils/api';

export type ReminderStatus = 'PENDING' | 'COMPLETED' | 'OVERDUE' | 'DISMISSED';

export interface ReminderRecipient {
  clientId: string;
  name: string;
  email: string | null;
  emailEnabled: boolean;
}

export interface ReminderLastNotice {
  // Ordinal slot, not the offset: 'pre-1'..'pre-5' | 'overdue-1'..'overdue-5' |
  // 'manual:<date>'. Render with stageLabel() — the key intentionally carries no
  // timing, because lead times are per-type and clinic-configurable (ADR-0020).
  stage: string;
  status: string; // claimed | sent | failed | skipped
  skipReason: string | null; // opt-out | no-email | undeliverable | superseded | send-error
  sentAt: string | null;
  emailStatus: string | null; // sent | delivered | opened | bounced
}

export interface DueListRow {
  id: string;
  horseId: string;
  clinicId: string;
  title: string;
  description?: string | null;
  dueDate: string;
  status: ReminderStatus;
  type?: string | null;
  notes?: string | null;
  declinedAt: string | null;
  sourceType?: string | null;
  horse: { id: string; name: string; barnId: string | null };
  barn: { id: string; name: string } | null;
  recipients: ReminderRecipient[];
  lastNotice: ReminderLastNotice | null;
}

export type RecipientPolicy = 'ALL_OWNERS' | 'PRIMARY_ONLY';

/** Per-reminder-type notice timing (ADR-0020): days before / after the due date. */
export interface TypeLeadTime {
  preDays: number[];
  overdueDays: number[];
  /** Absent = true. false = this type never auto-notices (ADR-0021). */
  enabled?: boolean;
}

/** Keys are lowercased reminder types; '_default' covers unknown/legacy ones. */
export type TypeLeadTimes = Record<string, TypeLeadTime>;

export const DEFAULT_TYPE_KEY = '_default';

/**
 * Human label for an ordinal stage key. The key deliberately carries no timing
 * (clinics tune the offsets), so this is the only place the wording lives.
 */
export function stageLabel(stage: string): string {
  if (stage.startsWith('manual:')) return `manual (${stage.slice('manual:'.length)})`;
  const pre = /^pre-(\d+)$/.exec(stage);
  if (pre) return `notice ${pre[1]}`;
  const overdue = /^overdue-(\d+)$/.exec(stage);
  if (overdue) return `overdue notice ${overdue[1]}`;
  return stage;
}

export interface ReminderSettings {
  clinicId: string;
  enabled: boolean;
  sendHourLocal: number;
  replyToEmail: string | null;
  backfillCutoffDays: number;
  recipientPolicy: RecipientPolicy;
  /** Resolved server-side: stored config merged over the built-in defaults. */
  typeLeadTimes: TypeLeadTimes;
  typeLeadTimesUpdatedAt: string | null;
  enabledAt: string | null;
  lastRunAt: string | null;
  lastRunError: string | null;
  /** SMS channel (ADR-0022): clinic opt-in + per-channel run marker. */
  smsEnabled: boolean;
  lastSmsRunAt: string | null;
  /** Platform capability: TWILIO_* provisioned. False = card shows "not yet available". */
  smsConfigured: boolean;
  /** Appointment reminders (ADR-0046): own switch, days before the visit (furthest first). */
  appointmentRemindersEnabled: boolean;
  appointmentLeadDays: number[];
  /** Client instructions keyed by lowercased visit type. */
  appointmentInstructions: Record<string, string>;
  /** False until the clinic's time zone is set; appointment reminders wait for it. */
  clinicTimezoneSet: boolean;
}

export interface UpdateReminderSettingsPayload {
  enabled?: boolean;
  sendHourLocal?: number;
  replyToEmail?: string;
  backfillCutoffDays?: number;
  recipientPolicy?: RecipientPolicy;
  smsEnabled?: boolean;
  /** Send `{}` to reset every type back to the built-in defaults. */
  typeLeadTimes?: TypeLeadTimes;
  appointmentRemindersEnabled?: boolean;
  appointmentLeadDays?: number[];
  appointmentInstructions?: Record<string, string>;
}

export const reminderService = {
  async getDueList(filters?: {
    status?: ReminderStatus;
    /** Scoping to one horse also lifts the clinic-wide date window server-side. */
    horseId?: string;
    from?: string;
    to?: string;
  }) {
    const r = await api.get<DueListRow[]>('/recall-reminders/due-list', { params: filters });
    return r.data;
  },

  async update(
    id: string,
    data: Partial<{
      title: string;
      description: string;
      dueDate: string;
      status: ReminderStatus;
      type: string;
      notes: string;
      declinedAt: string | null;
    }>,
  ) {
    const r = await api.put(`/recall-reminders/${id}`, data);
    return r.data;
  },

  async sendNow(id: string) {
    const r = await api.post<{ sent: number; skipped: number }>(
      `/recall-reminders/${id}/send-now`,
    );
    return r.data;
  },

  async getSettings() {
    const r = await api.get<ReminderSettings>('/recall-reminders/settings');
    return r.data;
  },

  async updateSettings(data: UpdateReminderSettingsPayload) {
    const r = await api.put<{ settings: ReminderSettings; backfill?: { created: number } }>(
      '/recall-reminders/settings',
      data,
    );
    return r.data;
  },

  /** Rendered sample of the real email template — never sends. */
  async getEmailPreview(kind?: 'appointment') {
    const r = await api.get<{ subject: string; html: string }>(
      '/recall-reminders/settings/email-preview',
      kind ? { params: { kind } } : undefined,
    );
    return r.data;
  },

  /** Sample email to the signed-in admin ([Test] prefix, 60s cooldown → 429). */
  async sendTestEmail() {
    const r = await api.post<{ sent: boolean; to: string }>(
      '/recall-reminders/settings/test-send',
    );
    return r.data;
  },
};
